import type { EffectType } from './index';
import type { SerializedEffectState } from '../types/serializable';
import type { CropBox } from '../CropBox';
import { ZoomEffect } from './ZoomEffect';
import { ViewportEffect } from './ViewportEffect';
import { CircleCoverEffect } from './CircleCoverEffect';
import { BorderEffect, BorderWidthUnit } from './BorderEffect';
import { OpacityEffect } from './OpacityEffect';
import { PanEffect } from './PanEffect';

interface Point {
    x: number;
    y: number;
}

enum SerializedEffectType {
    Zoom = 'zoom',
    Opacity = 'opacity',
    Pan = 'pan',
    Viewport = 'viewport',
    CircleCover = 'circleCover',
    Border = 'border'
}

const KNOWN_EFFECT_TYPES = new Set<string>(Object.values(SerializedEffectType));

function asRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object'
        ? value as Record<string, unknown>
        : null;
}

function getNumber(record: Record<string, unknown>, key: string): number | undefined {
    const value = record[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getString(record: Record<string, unknown>, key: string): string | undefined {
    const value = record[key];
    return typeof value === 'string' ? value : undefined;
}

function getBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
    const value = record[key];
    return typeof value === 'boolean' ? value : undefined;
}

function isPoint(value: unknown): value is Point {
    const record = asRecord(value);
    return !!record &&
        typeof record.x === 'number' &&
        Number.isFinite(record.x) &&
        typeof record.y === 'number' &&
        Number.isFinite(record.y);
}

function isCropBox(value: unknown): value is CropBox {
    const record = asRecord(value);
    return !!record &&
        typeof record.x === 'number' &&
        Number.isFinite(record.x) &&
        typeof record.y === 'number' &&
        Number.isFinite(record.y) &&
        typeof record.width === 'number' &&
        Number.isFinite(record.width) &&
        typeof record.height === 'number' &&
        Number.isFinite(record.height);
}

function isBorderUnit(value: unknown): value is BorderWidthUnit {
    return value === BorderWidthUnit.PX || value === BorderWidthUnit.PERCENT;
}

function isEffectInstance(value: unknown): value is EffectType {
    const record = asRecord(value);
    return !!record &&
        typeof record.calculateProgress === 'function' &&
        (typeof record.apply === 'function' || typeof record.getContainerStyle === 'function');
}

const KNOWN_SERIALIZED_EFFECT_FIELDS = new Set([
    'type',
    'duration',
    'at',
    'from',
    'to',
    'color',
    'width',
    'unit',
    'containerEffect'
]);

function copySerializableEffectProperties(
    source: Record<string, unknown>,
    target: SerializedEffectState,
    preserveReservedKeys: boolean = false
): SerializedEffectState {
    for (const [key, value] of Object.entries(source)) {
        if (
            typeof value !== 'function' &&
            value !== undefined &&
            (preserveReservedKeys || !KNOWN_SERIALIZED_EFFECT_FIELDS.has(key))
        ) {
            target[key] = value;
        }
    }
    return target;
}

function isKnownEffectType(type: string): boolean {
    return KNOWN_EFFECT_TYPES.has(type);
}

function addValidatedEffectFields(
    record: Record<string, unknown>,
    state: SerializedEffectState
): SerializedEffectState {
    const duration = getNumber(record, 'duration');
    const at = getNumber(record, 'at');
    if (duration !== undefined) state.duration = duration;
    if (at !== undefined) state.at = at;

    const from = record.from;
    const to = record.to;
    if (isPoint(from) || isCropBox(from) || (typeof from === 'number' && Number.isFinite(from))) state.from = from;
    if (isPoint(to) || isCropBox(to) || (typeof to === 'number' && Number.isFinite(to))) state.to = to;

    const color = getString(record, 'color');
    const width = getNumber(record, 'width');
    if (color !== undefined) state.color = color;
    if (width !== undefined) state.width = width;
    if (isBorderUnit(record.unit)) state.unit = record.unit;

    const containerEffect = getBoolean(record, 'containerEffect');
    if (containerEffect !== undefined) state.containerEffect = containerEffect;

    return state;
}

function createSerializedOnlyEffect(record: Record<string, unknown>): EffectType {
    const state = addValidatedEffectFields(
        record,
        copySerializableEffectProperties(
            record,
            { type: getString(record, 'type') ?? 'unknown' },
            true
        )
    );

    return {
        ...state,
        containerEffect: getBoolean(record, 'containerEffect') ?? false,
        calculateProgress: () => 1,
        apply: (_ctx, _dimensions, _progress, prev) => prev
    } as unknown as EffectType;
}

export function serializeEffect(effect: EffectType): SerializedEffectState | null {
    const record = effect as unknown as Record<string, unknown>;
    const type = getString(record, 'type');
    if (!type) {
        return null;
    }

    return addValidatedEffectFields(
        record,
        copySerializableEffectProperties(record, { type }, !isKnownEffectType(type))
    );
}

export function serializeEffects(effects: EffectType[] | undefined): SerializedEffectState[] {
    return (effects ?? [])
        .map(serializeEffect)
        .filter((effect): effect is SerializedEffectState => effect !== null);
}

export function deserializeEffect(state: unknown): EffectType | null {
    if (isEffectInstance(state)) {
        return state;
    }

    const record = typeof state === 'string'
        ? { type: state }
        : asRecord(state);
    if (!record) {
        return null;
    }

    const type = getString(record, 'type');
    const duration = getNumber(record, 'duration') ?? 0;
    const at = getNumber(record, 'at');

    switch (type) {
        case SerializedEffectType.Zoom: {
            const from = getNumber(record, 'from');
            const to = getNumber(record, 'to');
            return from !== undefined && to !== undefined
                ? new ZoomEffect(from, to, duration, at)
                : null;
        }
        case SerializedEffectType.Opacity: {
            const from = getNumber(record, 'from');
            const to = getNumber(record, 'to');
            return from !== undefined && to !== undefined
                ? new OpacityEffect(from, to, duration, at)
                : null;
        }
        case SerializedEffectType.Pan:
            return isPoint(record.from) && isPoint(record.to)
                ? new PanEffect(record.from, record.to, duration, at)
                : null;
        case SerializedEffectType.Viewport:
            return isCropBox(record.from) && isCropBox(record.to)
                ? new ViewportEffect(record.from, record.to, duration, at)
                : null;
        case SerializedEffectType.CircleCover:
            return new CircleCoverEffect(duration, at);
        case SerializedEffectType.Border:
            return new BorderEffect(
                getString(record, 'color') ?? '#000000',
                getNumber(record, 'width') ?? 1,
                isBorderUnit(record.unit) ? record.unit : BorderWidthUnit.PX,
                duration,
                at
            );
        default:
            return type ? createSerializedOnlyEffect(record) : null;
    }
}

export function deserializeEffects(states: readonly unknown[] | undefined): EffectType[] {
    return (states ?? [])
        .map(deserializeEffect)
        .filter((effect): effect is EffectType => effect !== null);
}
